import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from './store'
import { ConfirmDialogState } from '../components/ConfirmDialog'
import {
  CreativeDrawerState,
  CreativeDrawerView,
} from '../features/creative/CreativeDrawer'
import { AppQuery$data } from '../__generated__/AppQuery.graphql'
import { ClearPendingCreativeChangesConfirmDialogProps } from '../features/creative/ClearPendingCreativeChangesConfirmDialog'
import { CreativeDrawerMetaAd$key } from '../features/creative/__generated__/CreativeDrawerMetaAd.graphql'

export type Auth = { id: string; email: string; token: string; superuser: boolean; accountId: string }
export type Group = { id: string; name: string }

export interface AppState {
  auth: Auth | undefined
  viewingUserId: string | undefined
  userKey: AppQuery$data['getUser'] | undefined
  mediaPlanModalOpen: boolean
  mediaPlanManageUsersModalOpen: boolean
  archiveMediaPlanConfirmDialog: ConfirmDialogState
  campaignModalOpen: boolean
  deleteCampaignConfirmDialog: ConfirmDialogState
  mediaPlanAdvisorOpen: boolean
  creativeDrawerData: CreativeDrawerState
  creativeDrawerOpen: boolean
  deleteCommentConfirmDialog: ConfirmDialogState
  targetAudienceModalOpen: boolean
  creativeModalOpen: boolean
  deleteTargetAudienceConfirmDialog: ConfirmDialogState
  createMediaPlanLoading: boolean
  clearPendingCreativeChangesConfirmDialog: ClearPendingCreativeChangesConfirmDialogProps
  submitCreativeForApprovalConfirmDialog: ConfirmDialogState
}

export const initialAppState: AppState = {
  auth: undefined,
  viewingUserId: undefined,
  userKey: undefined,
  mediaPlanModalOpen: false,
  mediaPlanManageUsersModalOpen: false,
  deleteCampaignConfirmDialog: { open: false },
  campaignModalOpen: false,
  archiveMediaPlanConfirmDialog: { open: false },
  mediaPlanAdvisorOpen: false,
  creativeDrawerData: {
    view: CreativeDrawerView.TEXT,
    metaAdFragmentRequest: null,
  },
  creativeDrawerOpen: false,
  deleteCommentConfirmDialog: { open: false },
  targetAudienceModalOpen: false,
  creativeModalOpen: false,
  deleteTargetAudienceConfirmDialog: { open: false },
  createMediaPlanLoading: false,
  clearPendingCreativeChangesConfirmDialog: { open: false },
  submitCreativeForApprovalConfirmDialog: { open: false },
}

export const appSlice = createSlice({
  name: 'app',
  initialState: initialAppState,
  reducers: {
    setAuth: (state, action: PayloadAction<Auth | undefined>) => {
      state.auth = action.payload
    },
    setViewingUserId: (state, action: PayloadAction<string | undefined>) => {
      state.viewingUserId = action.payload
    },
    setUserKey: (
      state,
      action: PayloadAction<AppQuery$data['getUser'] | undefined>
    ) => {
      state.userKey = action.payload as Draft<AppQuery$data['getUser']>
    },
    setMediaPlanModalOpen: (state, action: PayloadAction<boolean>) => {
      state.mediaPlanModalOpen = action.payload
    },
    setMediaPlanManageUsersModalOpen: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.mediaPlanManageUsersModalOpen = action.payload
    },
    setCampaignModalOpen: (state, action: PayloadAction<boolean>) => {
      state.campaignModalOpen = action.payload
    },
    setArchiveMediaPlanConfirmDialog: (
      state,
      action: PayloadAction<ConfirmDialogState>
    ) => {
      state.archiveMediaPlanConfirmDialog = action.payload
    },
    setDeleteCampaignConfirmDialog: (
      state,
      action: PayloadAction<ConfirmDialogState>
    ) => {
      state.deleteCampaignConfirmDialog = action.payload
    },
    setMediaPlanAdvisorOpen: (state, action: PayloadAction<boolean>) => {
      state.mediaPlanAdvisorOpen = action.payload
    },
    setCreativeDrawerData: (
      state,
      action: PayloadAction<CreativeDrawerState>
    ) => {
      state.creativeDrawerData = {
        ...action.payload,
        metaAdFragmentRequest: action.payload
          ?.metaAdFragmentRequest as Draft<CreativeDrawerMetaAd$key>,
      }
    },
    setCreativeDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.creativeDrawerOpen = action.payload
    },
    setDeleteCommentConfirmDialog: (
      state,
      action: PayloadAction<ConfirmDialogState>
    ) => {
      state.deleteCommentConfirmDialog = action.payload
    },
    setTargetAudienceModalOpen: (state, action: PayloadAction<boolean>) => {
      state.targetAudienceModalOpen = action.payload
    },
    setCreativeModalOpen: (state, action: PayloadAction<boolean>) => {
      state.creativeModalOpen = action.payload
    },
    setDeleteTargetAudienceConfirmDialog: (
      state,
      action: PayloadAction<ConfirmDialogState>
    ) => {
      state.deleteTargetAudienceConfirmDialog = action.payload
    },
    setCreateMediaPlanLoading: (state, action: PayloadAction<boolean>) => {
      state.createMediaPlanLoading = action.payload
    },
    setClearPendingCreativeChangesConfirmDialog: (
      state,
      action: PayloadAction<ClearPendingCreativeChangesConfirmDialogProps>
    ) => {
      state.clearPendingCreativeChangesConfirmDialog = action.payload
    },
    setSubmitCreativeForApprovalConfirmDialog: (
      state,
      action: PayloadAction<ConfirmDialogState>
    ) => {
      state.submitCreativeForApprovalConfirmDialog = action.payload
    },
  },
})

export const selectAuth = (state: RootState) => state.app.auth
export const selectSuperuser = (state: RootState) => state.app.auth?.superuser ?? false
export const selectAccountId = (state: RootState) => state.app.auth?.accountId ?? ''
export const selectViewingUserId = (state: RootState) => state.app.viewingUserId
export const selectUserKey = (state: RootState) => state.app.userKey
export const selectMediaPlanAdvisorOpen = (state: RootState) =>
  state.app.mediaPlanAdvisorOpen
export const selectCreativeDrawerData = (state: RootState) =>
  state.app.creativeDrawerData
export const selectCreativeDrawerOpen = (state: RootState) =>
  state.app.creativeDrawerOpen
export const selectDeleteCommentConfirmDialog = (state: RootState) =>
  state.app.deleteCommentConfirmDialog
export const selectCreativeModalOpen = (state: RootState) =>
  state.app.creativeModalOpen
export const selectCreateMediaPlanLoading = (state: RootState) =>
  state.app.createMediaPlanLoading
export const selectClearPendingCreativeChangesConfirmDialog = (
  state: RootState
) => state.app.clearPendingCreativeChangesConfirmDialog
export const selectSubmitCreativeForApprovalConfirmDialog = (
  state: RootState
) => state.app.submitCreativeForApprovalConfirmDialog

export const {
  setAuth,
  setViewingUserId,
  setUserKey,
  setMediaPlanModalOpen,
  setCampaignModalOpen,
  setMediaPlanAdvisorOpen,
  setCreativeDrawerData,
  setCreativeDrawerOpen,
  setDeleteCommentConfirmDialog,
  setTargetAudienceModalOpen,
  setCreativeModalOpen,
  setClearPendingCreativeChangesConfirmDialog,
  setSubmitCreativeForApprovalConfirmDialog,
} = appSlice.actions

export default appSlice.reducer
